/**
 * DashboardPage — главный экран Директора (ФТ-ЭКРАН-002, ТЗ 4.2.7).
 *
 * Порядок блоков:
 *   1. KPI-плашка (компактные счётчики, кликабельные)
 *   2. Диаграмма Ганта (центрирована на Сегодня)
 *   3. Три зоны: Предпроект | В работе | Завершено в проектировании
 *   4. Блок "Требуют внимания" — просрочки + истекающие разделы
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import {
  FolderOpen,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  RefreshCw,
} from 'lucide-react'

import { dashboardApi } from '@/api/dashboard'
import { useEffectiveRole } from '@/store/auth'
import Spinner from '@/components/common/Spinner'
import ZoneCard from '@/components/dashboard/ZoneCard'
import AttentionBlock, { type AttentionFilter } from '@/components/dashboard/AttentionBlock'
import GanttChart from '@/components/projects/GanttChart'
import HelpButton from '@/components/common/HelpButton'

const GANTT_ANCHOR     = 'dashboard-gantt'
const ATTENTION_ANCHOR = 'attention-block'

export default function DashboardPage() {
  const qc       = useQueryClient()
  const navigate = useNavigate()
  const role     = useEffectiveRole()
  const isDirector = role === 'director' || role === 'deputy_director'
  const isDesigner = role === 'designer'
  const screenTitle = isDirector ? 'Главный экран' : isDesigner ? 'Мои разделы' : 'Мои проекты'
  const ganttTitle  = isDirector
    ? 'Диаграмма Ганта · В работе'
    : isDesigner ? 'Диаграмма Ганта · мои разделы' : 'Диаграмма Ганта · мои проекты'
  const [refreshing, setRefreshing]           = useState(false)
  const [attentionFilter, setAttentionFilter] = useState<AttentionFilter>('all')

  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['dashboard-zones'],
    queryFn: () => dashboardApi.zones().then((r) => r.data),
    refetchInterval: 20_000,
  })

  async function handleRefresh() {
    setRefreshing(true)
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['dashboard-zones'] }),
      qc.invalidateQueries({ queryKey: ['gantt'] }),
    ])
    setRefreshing(false)
  }

  function scrollTo(anchor: string) {
    const el = document.getElementById(anchor)
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  function scrollToAttention(filter: AttentionFilter) {
    setAttentionFilter(filter)
    // Небольшая задержка, чтобы React успел перерисовать до scroll
    setTimeout(() => scrollTo(ATTENTION_ANCHOR), 50)
  }

  if (isLoading) return <Spinner size="lg" />
  if (error || !data) {
    return <div className="p-6 text-rose-600">Ошибка загрузки данных</div>
  }

  const updatedAt = dataUpdatedAt ? new Date(dataUpdatedAt) : null

  return (
    <div className="p-4 sm:p-6 space-y-5">
      {/* Шапка */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{screenTitle}</h1>
          <p className="text-sm text-gray-500">
            Данные на {format(new Date(data.as_of), 'd MMMM yyyy', { locale: ru })}
            {updatedAt && (
              <span className="ml-2 text-gray-400">
                · обновлено в {format(updatedAt, 'HH:mm')}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton />
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Обновить
          </button>
        </div>
      </div>

      {/* KPI-плашка */}
      <div data-tour="kpi-block">
        <KpiStrip
          total={data.kpi.total}
          inProgress={data.kpi.in_progress}
          overdue={data.kpi.overdue_sections}
          dueSoon={data.kpi.due_soon}
          doneMonth={data.kpi.done_month}
          onClickInProgress={() => scrollTo(GANTT_ANCHOR)}
          onClickOverdue={() => scrollToAttention('overdue')}
          onClickDueSoon={() => scrollToAttention('due_soon')}
          onClickDoneMonth={() => navigate('/projects')}
        />
      </div>

      {/* Гант — для не-директора без фильтра по этапу (все его проекты), данные scoped на бэкенде */}
      <div data-tour="gantt-chart">
        <GanttChart
          stage={isDirector ? 'in_design' : undefined}
          title={ganttTitle}
          anchorId={GANTT_ANCHOR}
        />
      </div>

      {/* Три зоны — для проектировщика скрываем (он работает с разделами, не с зонами проектов) */}
      {!isDesigner && (
        <div className="grid grid-cols-1 md:grid-cols-[160px_1fr_160px] gap-4 items-start">
          <div data-tour="zone-preproject">
            <ZoneCard
              title="Предпроект"
              subtitle="Договор, заделы"
              zone={data.zones.pre_project}
              accent="left"
              compact
            />
          </div>
          <div data-tour="zone-active">
            <ZoneCard
              title="В работе"
              subtitle="Активное проектирование"
              zone={data.zones.in_design}
              accent="center"
              onCountClick={() => scrollTo(GANTT_ANCHOR)}
              onSeeAll={() => navigate('/projects')}
            />
          </div>
          <ZoneCard
            title="Завершены"
            subtitle="Согласования"
            zone={data.zones.post_design}
            accent="right"
            compact
          />
        </div>
      )}

      {/* Требуют внимания */}
      <div data-tour="attention-block">
        <AttentionBlock
          items={data.attention}
          filter={attentionFilter}
          onFilterChange={setAttentionFilter}
        />
      </div>
    </div>
  )
}

// ─── KPI Strip ────────────────────────────────────────────────────────────────

interface KpiStripProps {
  total: number
  inProgress: number
  overdue: number
  dueSoon: number
  doneMonth: number
  onClickInProgress?: () => void
  onClickOverdue?: () => void
  onClickDueSoon?: () => void
  onClickDoneMonth?: () => void
}

function KpiStrip({
  total,
  inProgress,
  overdue,
  dueSoon,
  doneMonth,
  onClickInProgress,
  onClickOverdue,
  onClickDueSoon,
  onClickDoneMonth,
}: KpiStripProps) {
  const items = [
    {
      label: 'Всего проектов',
      value: total,
      icon: <FolderOpen className="h-4 w-4 text-gray-400" />,
      bg: 'bg-white',
      onClick: undefined as (() => void) | undefined,
      ring: '',
    },
    {
      label: 'В работе',
      value: inProgress,
      icon: <Loader2 className="h-4 w-4 text-blue-500" />,
      bg: 'bg-blue-50',
      onClick: onClickInProgress,
      ring: 'hover:ring-2 hover:ring-blue-300',
    },
    {
      label: 'Скоро срок',
      value: dueSoon,
      icon: <Clock className="h-4 w-4 text-amber-500" />,
      bg: dueSoon > 0 ? 'bg-amber-50' : 'bg-white',
      onClick: dueSoon > 0 ? onClickDueSoon : undefined,
      ring: dueSoon > 0 ? 'hover:ring-2 hover:ring-amber-300' : '',
    },
    {
      label: 'Просрочено',
      value: overdue,
      icon: <AlertCircle className={`h-4 w-4 ${overdue > 0 ? 'text-rose-500' : 'text-gray-300'}`} />,
      bg: overdue > 0 ? 'bg-rose-50' : 'bg-white',
      onClick: overdue > 0 ? onClickOverdue : undefined,
      ring: overdue > 0 ? 'hover:ring-2 hover:ring-rose-300' : '',
    },
    {
      label: 'Принято за месяц',
      value: doneMonth,
      icon: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
      bg: doneMonth > 0 ? 'bg-emerald-50' : 'bg-white',
      onClick: doneMonth > 0 ? onClickDoneMonth : undefined,
      ring: doneMonth > 0 ? 'hover:ring-2 hover:ring-emerald-300' : '',
    },
  ]

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {items.map((c) => {
        const isClickable = !!c.onClick
        const Component = isClickable ? 'button' : 'div'
        return (
          <Component
            key={c.label}
            type={isClickable ? 'button' : undefined}
            onClick={c.onClick}
            className={[
              'rounded-xl border border-gray-200',
              c.bg,
              'px-4 py-3 shadow-sm text-left transition-all',
              isClickable
                ? `cursor-pointer hover:shadow-md ${c.ring} group`
                : '',
            ].join(' ')}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide leading-tight">
                {c.label}
              </span>
              {c.icon}
            </div>
            <div className="mt-1.5 text-2xl font-bold text-gray-900 tabular-nums">
              {c.value}
            </div>
            {isClickable && (
              <div className="mt-1 text-xs text-gray-400 group-hover:text-gray-600 transition-colors">
                Нажмите для перехода →
              </div>
            )}
          </Component>
        )
      })}
    </div>
  )
}
